import { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { MobileMap } from '@mobileTypes'

const STORAGE_KEY = 'mobileMaps'

const getMapsFromStorage = (): MobileMap[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    const raw = data ? JSON.parse(data) : []

    return Array.isArray(raw)
      ? raw.map((m: MobileMap) => ({
          ...m,
          mapCenter: Array.isArray(m.mapCenter) ? m.mapCenter : [37.6, 55.75],
        }))
      : []
  } catch (error) {
    console.error('Error reading mobile maps from localStorage:', error)

    return []
  }
}

const saveMapsToStorage = (maps: MobileMap[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(maps))
  } catch (error) {
    console.error('Error saving mobile maps to localStorage:', error)
  }
}

export const useMobileMaps = () => {
  const [maps, setMaps] = useState<MobileMap[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadMaps = () => {
      setLoading(true)
      const loadedMaps = getMapsFromStorage()
      setMaps(loadedMaps)
      setLoading(false)
    }

    loadMaps()
  }, [])

  const createMap = useCallback(
    (mapData: Omit<MobileMap, 'id' | 'createdAt' | 'updatedAt'>): MobileMap => {
      const newMap: MobileMap = {
        ...mapData,
        id: uuidv4(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }

      const allMaps = getMapsFromStorage()
      const updatedMaps = [...allMaps, newMap]
      setMaps(updatedMaps)
      saveMapsToStorage(updatedMaps)

      return newMap
    },
    []
  )

  const updateMap = useCallback((id: string, mapData: Partial<MobileMap>) => {
    const allMaps = getMapsFromStorage()
    const updatedMaps = allMaps.map(map =>
      map.id === id
        ? {
            ...map,
            ...mapData,
            id: map.id,
            createdAt: map.createdAt,
            updatedAt: Date.now(),
          }
        : map
    )

    setMaps(updatedMaps)
    saveMapsToStorage(updatedMaps)
  }, [])

  const deleteMap = useCallback((id: string) => {
    const allMaps = getMapsFromStorage()
    const updatedMaps = allMaps.filter(map => map.id !== id)
    setMaps(updatedMaps)
    saveMapsToStorage(updatedMaps)
  }, [])

  const getMapById = useCallback((id: string): MobileMap | undefined => {
    const allMaps = getMapsFromStorage()

    return allMaps.find(map => map.id === id)
  }, [])

  const copyMap = useCallback((id: string, newName: string): MobileMap | null => {
    const allMaps = getMapsFromStorage()
    const mapToCopy = allMaps.find(map => map.id === id)
    if (!mapToCopy) return null

    const newMap: MobileMap = {
      ...mapToCopy,
      id: uuidv4(),
      name: newName,
      internalId: mapToCopy.internalId + '_copy',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }

    const updatedMaps = [...allMaps, newMap]
    setMaps(updatedMaps)
    saveMapsToStorage(updatedMaps)

    return newMap
  }, [])

  return {
    maps,
    loading,
    createMap,
    updateMap,
    deleteMap,
    getMapById,
    copyMap,
  }
}
